import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import ReactECharts from 'echarts-for-react';
import Pill from '../components/ui/Pill';
import MiniBar from '../components/ui/MiniBar';
import PageHeader from '../components/ui/PageHeader';
import StatCard from '../components/ui/StatCard';
import ActionButton from '../components/ui/ActionButton';
import ChartCard from '../components/ui/ChartCard';
import { dataFetch } from '../api';
import CreateSimulationDrawer from '../components/phishing/CreateSimulationDrawer';

const { FiPlus, FiLoader } = FiIcons;

const Phishing = () => {
  const [simulations, setSimulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const fetchSimulations = async () => {
    setLoading(true);
    const res = await dataFetch('read/phishing_simulations');
    if (res?.status === 'success') {
      setSimulations(res.data || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSimulations();
  }, []);

  const totalSims = simulations.length;
  // A simulation is considered active if it was sent within the last 30 days
  const activeSims = simulations.filter(s => new Date(s.date) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)).length;
  
  let avgClick = 0;
  let avgReport = 0;
  
  if (totalSims > 0) {
    const totalSent = simulations.reduce((a, b) => a + (b.sent || 0), 0);
    const totalClicked = simulations.reduce((a, b) => a + (b.clicked || 0), 0);
    const totalReported = simulations.reduce((a, b) => a + (b.reported || 0), 0);
    if (totalSent > 0) {
      avgClick = ((totalClicked / totalSent) * 100).toFixed(1);
      avgReport = ((totalReported / totalSent) * 100).toFixed(1);
    }
  }

  const chartOption = {
    tooltip: { trigger: 'axis' },
    legend: { data: ['Click Rate %', 'Report Rate %'], bottom: 0, textStyle: { fontSize: 11 } },
    grid: { left: '3%', right: '4%', bottom: '18%', top: '5%', containLabel: true },
    xAxis: { type: 'category', data: simulations.map(s => (s.name || '').split(':')[0].trim()), axisLabel: { fontSize: 10, rotate: 8 } },
    yAxis: { type: 'value', axisLabel: { formatter: '{value}%', fontSize: 10 }, splitLine: { lineStyle: { color: '#f1f5f9' } } },
    series: [
      { 
        name: 'Click Rate %', 
        type: 'bar', 
        data: simulations.map(s => s.sent ? ((s.clicked / s.sent) * 100).toFixed(1) : 0), 
        itemStyle: { color: '#ef4444', borderRadius: [4, 4, 0, 0] }, 
        barMaxWidth: 28 
      },
      { 
        name: 'Report Rate %', 
        type: 'bar', 
        data: simulations.map(s => s.sent ? ((s.reported / s.sent) * 100).toFixed(1) : 0), 
        itemStyle: { color: '#10b981', borderRadius: [4, 4, 0, 0] }, 
        barMaxWidth: 28 
      },
    ],
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6 max-w-[1400px] mx-auto">
      <CreateSimulationDrawer 
        isOpen={isDrawerOpen} 
        onClose={() => setIsDrawerOpen(false)} 
        onSuccess={fetchSimulations} 
      />

      <PageHeader
        eyebrow="Security"
        title="Phishing Simulations"
        description="Monitor simulation results, click rates and learner reporting behavior."
        action={<ActionButton icon={FiPlus} label="Create Simulation" onClick={() => setIsDrawerOpen(true)} />}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Simulations" value={totalSims} />
        <StatCard label="Avg Click Rate" value={`${avgClick}%`} valueColor="text-red-500" sub="Lower is better" />
        <StatCard label="Avg Report Rate" value={`${avgReport}%`} valueColor="text-emerald-500" sub="Higher is better" />
        <StatCard label="Active" value={activeSims} valueColor="text-cyan-600" />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-400 font-bold bg-white">
                <th className="px-6 py-4">Simulation Name</th>
                <th className="px-6 py-4">Template</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Sent</th>
                <th className="px-6 py-4">Click Rate</th>
                <th className="px-6 py-4">Report Rate</th>
                <th className="px-6 py-4">Send Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-slate-500">
                    <SafeIcon icon={FiLoader} className="animate-spin text-2xl mx-auto mb-2" />
                    <p className="mt-2 text-sm">Loading simulations...</p>
                  </td>
                </tr>
              ) : simulations.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-slate-500">
                    No simulations found. Click "Create Simulation" to get started.
                  </td>
                </tr>
              ) : simulations.map(sim => {
                const cRate = sim.sent ? Math.round((sim.clicked / sim.sent) * 100) : 0;
                const rRate = sim.sent ? Math.round((sim.reported / sim.sent) * 100) : 0;
                const clickColor = cRate > 15 ? 'bg-red-500' : cRate > 8 ? 'bg-amber-500' : 'bg-emerald-500';
                const reportColor = rRate > 30 ? 'bg-emerald-500' : 'bg-blue-500';
                
                const isActive = new Date(sim.date) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);

                return (
                  <tr key={sim.id} className="hover:bg-slate-50 transition-colors group bg-white">
                    <td className="px-6 py-4">
                      <p className="font-semibold text-slate-900">{sim.name}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-[11px] text-slate-500">{sim.type}</p>
                    </td>
                    <td className="px-6 py-4">
                      <Pill status={isActive ? 'Active' : 'Completed'} />
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-semibold text-slate-700">{sim.sent}</span>
                      <span className="text-[11px] text-slate-400 ml-1">recipients</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <span className={`text-sm font-bold ${cRate > 15 ? 'text-red-600' : 'text-amber-600'}`}>{cRate}%</span>
                        <MiniBar percent={cRate} colorClass={clickColor} />
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <span className="text-sm font-bold text-emerald-600">{rRate}%</span>
                        <MiniBar percent={rRate} colorClass={reportColor} />
                      </div>
                    </td>
                    <td className="px-6 py-4 text-xs text-slate-500">
                      {new Date(sim.date).toLocaleDateString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-3 border-t border-slate-100 text-xs text-slate-400 font-medium bg-slate-50">
          {simulations.length} simulations
        </div>
      </div>

      {simulations.length > 0 && (
        <ChartCard title="Click Rate vs. Report Rate" subtitle="Across all simulations">
          <ReactECharts option={chartOption} style={{ height: 240 }} />
        </ChartCard>
      )}
    </motion.div>
  );
};

export default Phishing;